import { Suspense, lazy } from "react";
import { createBrowserRouter, Link, Navigate, useLocation } from "react-router-dom";
import { detectHost, type HostKind } from "@/lib/host";
import { AdminShell } from "@/components/layout/AdminShell";
import { RequireAuth } from "@/components/layout/RequireAuth";
import { RequireAdmin } from "@/components/layout/RequireAdmin";
import { RequireSuperAdmin } from "@/components/layout/RequireSuperAdmin";
import { RequireValidSlug } from "@/components/layout/RequireValidSlug";
import { TenantContextProvider } from "@/components/layout/TenantContextProvider";

// ---------------------------------------------------------------------------
// v1 tree — PARKED as of PR4 ("the flip"), and lazy()-imported as a consequence.
//
// v1 no longer serves the tenant root; it is reachable only at `/:tenantSlug/v1/*`
// and the parked auth mounts (`/v1/login`, `/auth/v1/signup`, `/v1/verify`,
// `/:tenantSlug/v1/<auth screen>`). It is NOT deleted and NOT unreachable — the
// whole tree stays routable so v1 can be resurrected.
//
// Because ~zero sessions now touch v1, static-importing it would park
// a tree in the entry chunk that nobody downloads it for. lazy() moves v1 out of
// the default bundle while leaving every v1 URL working — the mirror image of
// what these imports were before the flip.
//
// EXCEPTION — `ForcedPasswordChange` stays a STATIC import: it is shared with the
// admin tree (`/admin/change-password`, in `adminRoutes` below). lazy()-ing it
// would force a <LazyRoute> wrapper onto an /admin route, and /admin is out of
// scope for this PR (it stays v1 permanently and is deliberately untouched).
// ---------------------------------------------------------------------------

// Auth pages (v1, parked)
const Login = lazy(() => import("@/pages/auth/Login"));
const Signup = lazy(() => import("@/pages/auth/Signup"));
const VerifyEmail = lazy(() => import("@/pages/auth/VerifyEmail"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const AcceptInvite = lazy(() => import("@/pages/auth/AcceptInvite"));
// WS-E — forced temp-password rotation (reached via the global apiClient 403
// `password_change_required` interceptor, or direct navigation). Registered as
// a sibling auth route (NOT inside RequireAuth/AppShell) so the flagged user's
// /auth/me — which itself 403s while flagged — never fires here and bounces.
// Static (not lazy) — shared with the admin tree; see the note above.
import ForcedPasswordChange from "@/pages/auth/ForcedPasswordChange";
// v1 app shell (parked).
const AppShell = lazy(() =>
  import("@/components/layout/AppShell").then((m) => ({ default: m.AppShell })),
);
// passkey-v2 P0 loop fix: the tenant MFA verify/enroll surfaces are no longer
// dedicated routes — they are a modal (``MfaVerifyModal``) owned by the Login
// page. The old ``/:slug/login/mfa-{verify,enroll}`` routes + pages were
// deleted (their navigate-on-null-token effect was the login loop).

// Public (v1 SlugLanding is parked; NotFound is shared/global — stays static)
const SlugLanding = lazy(() => import("@/pages/public/SlugLanding"));
import NotFound from "@/pages/NotFound";

// Legal — public in-app document viewer (no auth; reached mid-activation).
import {
  AcceptableUsePolicyPage,
  AccountDataPrivacyNoticePage,
  TermsOfServicePage,
  PrivacyPolicyPage,
  DataProcessingAddendumPage,
} from "@/pages/legal/LegalDoc";

// App pages (v1, parked — all lazy; see the v1 note above)
const Dashboard = lazy(() => import("@/pages/app/Dashboard"));
// B-14 Phase 2 — Hiring Intelligence Dashboard.
const IntelligenceDashboard = lazy(() => import("@/pages/app/IntelligenceDashboard"));
// B-17 — Hiring Journal.
const HiringJournal = lazy(() => import("@/pages/app/HiringJournal"));
const Candidates = lazy(() => import("@/pages/app/Candidates"));
const CandidatePage = lazy(() => import("@/pages/app/CandidatePage"));
// Batch 30e — JD CRUD + new-candidate write paths.
// Phase 2.3 — JdDetail added; JdEdit moved to /jds/:jdId/edit.
const Jds = lazy(() => import("@/pages/app/Jds"));
const JdNew = lazy(() => import("@/pages/app/JdNew"));
const JdDetail = lazy(() => import("@/pages/app/JdDetail"));
const JdEdit = lazy(() => import("@/pages/app/JdEdit"));
const CandidateNew = lazy(() => import("@/pages/app/CandidateNew"));
const Profile = lazy(() => import("@/pages/app/Settings/Profile"));
const Team = lazy(() => import("@/pages/app/Settings/Team"));
const Sessions = lazy(() => import("@/pages/app/Settings/Sessions"));
const Billing = lazy(() => import("@/pages/app/Settings/Billing"));
const Usage = lazy(() => import("@/pages/app/Settings/Usage"));
const Activity = lazy(() => import("@/pages/app/Settings/Activity"));
const AI = lazy(() => import("@/pages/app/Settings/AI"));
const Compliance = lazy(() => import("@/pages/app/Settings/Compliance"));
const AIOversight = lazy(() => import("@/pages/app/Settings/AIOversight"));
const Integrations = lazy(() => import("@/pages/app/Settings/Integrations"));
const Roles = lazy(() => import("@/pages/app/Settings/Roles"));
const Security = lazy(() => import("@/pages/app/Settings/Security"));
// App-shell nav declutter — Settings hub (replaces the index→profile redirect).
const SettingsHub = lazy(() => import("@/pages/app/SettingsHub"));

// Admin (Batch 21a-3 — super-admin console)
import AdminLogin from "@/pages/admin/Login";
// passkey-v2 P0 loop fix: admin MFA verify/enroll are now a modal owned by
// AdminLogin (``MfaVerifyModal``), not dedicated routes. The old
// ``/admin/login/mfa-{verify,enroll}`` routes + pages were deleted.
import AdminTenants from "@/pages/admin/Tenants";
import AdminTenantCreate from "@/pages/admin/TenantCreate";
import AdminTenantDetail from "@/pages/admin/TenantDetail";
import AdminModelOverrides from "@/pages/admin/ModelOverrides";
import AdminBiasGuardrails from "@/pages/admin/BiasGuardrails";
import AdminGlobalSettingsUnavatar from "@/pages/admin/GlobalSettingsUnavatar";
import AdminTriage from "@/pages/admin/Triage";
import AdminIntegrations from "@/pages/admin/Integrations";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminPlatformHealth from "@/pages/admin/PlatformHealth";
import AdminProfile from "@/pages/admin/Profile";
// RBAC FE WS-D — super-admin users console (SA-host only; SA short-circuit +
// impersonation-hidden nav per architect_design_rbac_frontend.md §3 #4/#8).
import AdminSuperAdmins from "@/pages/admin/SuperAdmins";
import AdminDemoReset from "@/pages/admin/DemoReset";
import AdminDemoList from "@/pages/admin/demos/DemoList";
import AdminDemoProvision from "@/pages/admin/demos/DemoProvision";
import AdminDemoDetail from "@/pages/admin/demos/DemoDetail";

// Phase 1 — /dev/primitives showcase (HoD acceptance surface, no auth guard).
import DevPrimitives from "@/pages/dev/Primitives";

// UI-v2 — the DEFAULT tenant UX (PR4 "the flip"). v2 now serves the tenant root
// `/:tenantSlug/*`; v1 is parked at `/:tenantSlug/v1/*`. AppShellV2 sets its own
// local `data-theme="v2"` scope (NOT via ThemeContext).
//
// EAGER vs LAZY — inverted by PR4, and the inversion is the whole point.
// Pre-flip, v2 was the side tree, so lazy() was right: v2 cost the default
// bundle nothing. Post-flip v2 IS the default, and keeping the default UX behind
// lazy() would buy a Suspense flash on first paint for every user while saving
// nothing — the chunk is fetched immediately on literally every session.
//
// So: the shell + the high-traffic screens below are STATIC imports.
// `IntelligenceDashboardV2` stays lazy() and keeps its <LazyRoute> — Recharts
// (~420 kB, verified: it appears in NO other chunk) is imported ONLY there, via
// IntelChartsV2. That single route-level split is what actually keeps Recharts
// out of the entry chunk; it carries 100% of the real bundle benefit, and the
// intelligence route is the one screen where a brief spinner is expected anyway.
// The remaining low-traffic v2 screens (jd detail/new/edit, candidate-new,
// journal, settings/*) also stay lazy: they are not on the first-paint path, so
// eager-importing them would grow the entry chunk for no UX gain.
import { AppShellV2 } from "@/components/v2/layout/AppShellV2";
import DashboardV2 from "@/pages/v2/DashboardV2";
// UI-v2 Phase 1b-rem — Candidates + CandidatePage + Intelligence screens.
import CandidatesV2 from "@/pages/v2/CandidatesV2";
import CandidatePageV2 from "@/pages/v2/CandidatePageV2";
// Recharts lives ONLY behind this route — keep it lazy. See the note above.
const IntelligenceDashboardV2 = lazy(() => import("@/pages/v2/IntelligenceDashboardV2"));
// PP-4 — Outcomes overview (post-placement tracking). Static import: it is an
// INSIGHTS-group screen with no heavy chart deps (no Recharts), so it costs the
// entry chunk little and skips the Suspense flash, matching CandidatesV2/JdsV2.
import OutcomesOverviewV2 from "@/pages/v2/outcomes/OutcomesOverviewV2";
// PP-5 — Placed-candidate outcome view (Screen 2). Lazy: it pulls Recharts (the
// performance-signal line), so keep it OFF the outcomes-overview / entry chunk —
// same rationale as IntelligenceDashboardV2 above.
const PlacedCandidateOutcomeV2 = lazy(
  () => import("@/pages/v2/outcomes/PlacedCandidateOutcomeV2"),
);
// PP-7 — Model accuracy / calibration curve (Screen 3). Lazy: it pulls Recharts
// (the calibration curve), so keep it OFF the outcomes-overview / entry chunk —
// same rationale as PlacedCandidateOutcomeV2 / IntelligenceDashboardV2 above.
const ModelAccuracyV2 = lazy(() => import("@/pages/v2/outcomes/ModelAccuracyV2"));
// UI-v2 Phase 1c — Jobs + CandidateNew + HiringJournal screens.
import JdsV2 from "@/pages/v2/JdsV2";
const JdDetailV2 = lazy(() => import("@/pages/v2/JdDetailV2"));
const JdNewV2 = lazy(() => import("@/pages/v2/JdNewV2"));
const JdEditV2 = lazy(() => import("@/pages/v2/JdEditV2"));
const CandidateNewV2 = lazy(() => import("@/pages/v2/CandidateNewV2"));
const HiringJournalV2 = lazy(() => import("@/pages/v2/HiringJournalV2"));
// In-app Help Center — renders the authored `docs/help/` corpus. Lazy so the
// bundled markdown corpus stays OUT of the entry chunk (Help is low-traffic).
const HelpCenterV2 = lazy(() => import("@/pages/v2/help/HelpCenterV2"));
// UI-v2 Phase 1d — Settings hub + all settings screens (lazy for bundle isolation).
const SettingsHubV2 = lazy(() => import("@/pages/v2/settings/SettingsHubV2"));
const ProfileV2 = lazy(() => import("@/pages/v2/settings/ProfileV2"));
const SecurityV2 = lazy(() => import("@/pages/v2/settings/SecurityV2"));
const TeamV2 = lazy(() => import("@/pages/v2/settings/TeamV2"));
const BillingV2 = lazy(() => import("@/pages/v2/settings/BillingV2"));
const IntegrationsV2 = lazy(() => import("@/pages/v2/settings/IntegrationsV2"));
const AIV2 = lazy(() => import("@/pages/v2/settings/AIV2"));
const ComplianceV2 = lazy(() => import("@/pages/v2/settings/ComplianceV2"));
const BrandingV2 = lazy(() => import("@/pages/v2/settings/BrandingV2"));
const AIOversightV2 = lazy(() => import("@/pages/v2/settings/AIOversightV2"));
const UsageV2 = lazy(() => import("@/pages/v2/settings/UsageV2"));
const ActivityV2 = lazy(() => import("@/pages/v2/settings/ActivityV2"));
const SessionsV2 = lazy(() => import("@/pages/v2/settings/SessionsV2"));
const RolesV2 = lazy(() => import("@/pages/v2/settings/RolesV2"));

// UI-v2 Phase 1e — Auth + public v2 screens. STATIC imports as of PR4: these are
// now the product's front door (`/login`, `/auth/signup`, `/verify`,
// `/:tenantSlug/login`, …), and they are the FIRST thing an unauthenticated,
// cold-cache visitor loads — the exact moment a Suspense flash is most visible
// and least affordable. They are also what every backend-emitted email link
// (welcome / reset-password / invite / verify) lands on. Each screen is a
// standalone AuthShellV2 wrapper — NOT inside AppShellV2; they carry their own
// data-theme="v2" scope root.
import LoginV2 from "@/pages/v2/auth/LoginV2";
import SignupV2 from "@/pages/v2/auth/SignupV2";
import VerifyEmailV2 from "@/pages/v2/auth/VerifyEmailV2";
import ForgotPasswordV2 from "@/pages/v2/auth/ForgotPasswordV2";
import ResetPasswordV2 from "@/pages/v2/auth/ResetPasswordV2";
import AcceptInviteV2 from "@/pages/v2/auth/AcceptInviteV2";
import ForcedPasswordChangeV2 from "@/pages/v2/auth/ForcedPasswordChangeV2";
import SlugLandingV2 from "@/pages/v2/public/SlugLandingV2";

// B-14 Phase 1 — Hiring Intelligence Dashboard design-spike mocks.
// Three meaningfully-distinct clickable mocks served at
// `/__mocks/dashboard/{a,b,c}` behind NO auth so the founder can click
// through them on prod after merge. Each mock is code-split via lazy() so
// the main bundle is not bloated. Sandbox surface only — placeholder data,
// no API, no real tenant context; safe to leave unauthenticated. Phase 2
// engineer execution is blocked until the founder picks one mock; see
// `backend/docs/cofounder_brief_hiring_intelligence_dashboard.md`.
const MockDashboardA = lazy(() => import("@/mocks/dashboard/a/MockDashboardA"));
const MockDashboardB = lazy(() => import("@/mocks/dashboard/b/MockDashboardB"));
const MockDashboardC = lazy(() => import("@/mocks/dashboard/c/MockDashboardC"));

// B-24a Phase 1 — Super-Admin Dashboard design-spike mocks. Same pattern as
// B-14: three meaningfully-distinct clickable mocks served at
// `/__mocks/admin-dashboard/{a,b,c}` behind NO auth so the founder can click
// through them on prod after merge. Each is code-split via lazy() so the main
// bundle is not bloated. Sandbox surface only — placeholder platform data, no
// API, no real tenant context; safe to leave unauthenticated. Phase 2 (B-24b)
// engineer execution is blocked until the founder picks elements; see
// `backend/docs/hod_brief_b24a_sa_dashboard.md`.
const MockAdminDashboardA = lazy(
  () => import("@/mocks/admin-dashboard/a/MockAdminDashboardA"),
);
const MockAdminDashboardB = lazy(
  () => import("@/mocks/admin-dashboard/b/MockAdminDashboardB"),
);
const MockAdminDashboardC = lazy(
  () => import("@/mocks/admin-dashboard/c/MockAdminDashboardC"),
);

// eslint-disable-next-line react-refresh/only-export-components
function LazyRoute({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div
          data-testid="lazy-route-loading"
          className="flex min-h-screen items-center justify-center bg-surface-canvas text-text-tertiary"
        >
          Loading…
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

/**
 * PR4 compat — `/…/v2/…` → the canonical root path.
 *
 * v2 used to live under a `/v2` segment; post-flip it IS the root, so every old
 * `/v2` URL must keep working: the founder's demo bookmarks, deep links shared
 * internally, and anything already in someone's history. Strips the FIRST `/v2`
 * segment and re-navigates with `replace` (the old URL should not linger in the
 * back stack — pressing Back from a redirect that stayed in history traps the
 * user in a redirect loop).
 *
 * One component serves every v2 mount — `/:tenantSlug/v2/*`, `/v2/login`,
 * `/v2/verify`, `/auth/v2/signup` — because the rewrite is purely positional.
 * `search`/`hash` are carried over: `/acme/v2/reset-password?token=…` must not
 * lose the token, or the redirect silently breaks the very flow it preserves.
 *
 * The regex is anchored to a whole segment (`(?=\/|$)`), so a `/v2` appearing as
 * DATA deeper in the path (a JD literally id'd "v2") is untouched.
 */
// eslint-disable-next-line react-refresh/only-export-components
function V2CompatRedirect(): JSX.Element {
  const { pathname, search, hash } = useLocation();
  const stripped = pathname.replace(/\/v2(?=\/|$)/, "");
  return <Navigate to={`${stripped || "/"}${search}${hash}`} replace />;
}

/**
 * PR4 compat — `/:tenantSlug/jds/*` → `/:tenantSlug/jobs/*`.
 *
 * NOT a prefix swap — a URL CONTRACT CHANGE. v1 called this surface `jds`; v2
 * renamed it to `jobs` and `jobs` is canonical (v2 is not being renamed back).
 * The flip therefore silently invalidates every `/acme/jds/:id` bookmark, email
 * link, and browser-history entry in existence: the root now serves v2, v2 has
 * no `jds` route, so without this they hard-404. That is the single most likely
 * way a real user notices the flip as breakage.
 *
 * Mounted OUTSIDE the v2 shell so the redirect fires before the auth/shell stack
 * mounts — no shell flash, and an unauthenticated visitor still lands on the
 * right post-login target rather than bouncing through a stale URL.
 */
// eslint-disable-next-line react-refresh/only-export-components
function JdsCompatRedirect(): JSX.Element {
  const { pathname, search, hash } = useLocation();
  const next = pathname.replace(/^\/([^/]+)\/jds(?=\/|$)/, "/$1/jobs");
  return <Navigate to={`${next}${search}${hash}`} replace />;
}

// eslint-disable-next-line react-refresh/only-export-components
function MockIndex() {
  return (
    <div className="min-h-screen bg-surface-canvas px-content-px py-6 text-text-primary">
      <div className="mx-auto max-w-[640px]">
        <p className="text-2xs font-bold uppercase tracking-[0.14em] text-accent-secondary">
          B-14 Phase 1 · design spike
        </p>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-text-emphasis">
          Hiring Intelligence Dashboard — three mocks
        </h1>
        <p className="mt-2 text-sm text-text-secondary">
          Three meaningfully-distinct clickable React mocks. Pick one (or pick a
          composite) before Phase 2 engineer spawn. Each mock embodies a different
          design philosophy against the same brief and the same placeholder data.
        </p>
        <ul className="mt-6 space-y-3">
          {[
            {
              id: "a",
              title: "Mock A — Linear Analytics",
              tagline: "Dense, keyboard-first, restrained. Every pixel earns its place.",
            },
            {
              id: "b",
              title: "Mock B — Vercel Observability",
              tagline:
                "Hero panel + supporting grid. Dark-polish, full-saturation brand confidence.",
            },
            {
              id: "c",
              title: "Mock C — Notion Soft Analytics",
              tagline:
                "Spacious, customer-friendly, document-style. Reads like a hiring report.",
            },
          ].map((m) => (
            <li key={m.id}>
              <Link
                to={`/__mocks/dashboard/${m.id}`}
                className="block rounded-xl border border-border-default bg-surface-card p-4 transition hover:border-accent-secondary hover:bg-surface-card-hover"
              >
                <p className="text-base font-semibold text-text-primary">
                  {m.title}
                </p>
                <p className="mt-1 text-xs text-text-secondary">{m.tagline}</p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
function AdminMockIndex() {
  return (
    <div className="min-h-screen bg-surface-canvas px-content-px py-6 text-text-primary">
      <div className="mx-auto max-w-[640px]">
        <p className="text-2xs font-bold uppercase tracking-[0.14em] text-accent-secondary">
          B-24a Phase 1 · design spike
        </p>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-text-emphasis">
          Super-Admin Dashboard — three mocks
        </h1>
        <p className="mt-2 text-sm text-text-secondary">
          Three meaningfully-distinct clickable React mocks for the platform
          operator's front door. Same 6 KPIs, same platform dataset, same B-22
          AI-quality signals — they differ in how situational awareness is
          composed. Pick elements (or a composite) before B-24b implementation.
        </p>
        <ul className="mt-6 space-y-3">
          {[
            {
              id: "a",
              title: "Mock A — Operator Console",
              tagline:
                "Dense equal-weight grid + ⌘K command palette. The daily cockpit. Nothing hero, nothing hidden.",
            },
            {
              id: "b",
              title: "Mock B — Narrative Panels",
              tagline:
                "Platform AI Quality as the hero (pass-rate trend + per-category bias rejections). Best for screenshotting health.",
            },
            {
              id: "c",
              title: "Mock C — Risk-First (light parity)",
              tagline:
                "Exception lane first; healthy collapses to toggles; a calm 'All clear' empty state. Honors 'is anything on fire?'",
            },
          ].map((m) => (
            <li key={m.id}>
              <Link
                to={`/__mocks/admin-dashboard/${m.id}`}
                className="block rounded-xl border border-border-default bg-surface-card p-4 transition hover:border-accent-secondary hover:bg-surface-card-hover"
              >
                <p className="text-base font-semibold text-text-primary">
                  {m.title}
                </p>
                <p className="mt-1 text-xs text-text-secondary">{m.tagline}</p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const host: HostKind = detectHost();

// ---------------------------------------------------------------------------
// v1 tree — PARKED at /:tenantSlug/v1/* (UI-v2 migration; parked by PR3, and as
// of PR4 this is v1's ONLY mount).
//
// PR3 mounted v1 twice — at the root `/:tenantSlug/*` and at `/:tenantSlug/v1/*`
// — as a deliberately additive step. PR4 (this one) removes the ROOT mount and
// points it at v2. v1 keeps the parked mount and is NOT deleted: the tree stays
// fully routable so v1 can be resurrected.
//
// `v1Children` is still a shared binding rather than an inline table because the
// parked mount must stay a real mount (v1's full surface), not a shim — and
// because `router.dualMount.test.ts` asserts against these bindings by identity.
//
// Shell chrome is prefix-aware via `useTenantV1Base()`, which now always resolves
// to `/${slug}/v1` since that is the only mount left. That hook is what keeps a
// resurrected v1 self-consistent — its links keep users inside the parked tree
// instead of ejecting them into v2 on the first click.
//
// The shell is wrapped in <LazyRoute> because `AppShell` is now lazy() (v1 is off
// the default bundle). Each child ALSO carries its own <LazyRoute>: the nearest
// Suspense boundary wins, so a child's chunk loads without blanking the shell —
// the shell stays mounted, matching how v1 behaved when it was static.
const v1ShellElement = (
  <RequireValidSlug>
    <TenantContextProvider>
      <RequireAuth>
        <LazyRoute>
          <AppShell />
        </LazyRoute>
      </RequireAuth>
    </TenantContextProvider>
  </RequireValidSlug>
);

/** Wrap a parked-v1 page in its own Suspense boundary (all v1 pages are lazy). */
const v1 = (node: React.ReactNode): JSX.Element => <LazyRoute>{node}</LazyRoute>;

const v1Children = [
    { index: true, element: <Navigate to="dashboard" replace /> },
    { path: "dashboard", element: v1(<Dashboard />) },
    // B-14 Phase 2 — Hiring Intelligence Dashboard.
    { path: "intelligence", element: v1(<IntelligenceDashboard />) },
    // B-17 — Hiring Journal.
    { path: "journal", element: v1(<HiringJournal />) },
    { path: "candidates", element: v1(<Candidates />) },
    // Batch 30e — `/candidates/new` MUST come BEFORE `/candidates/:id`
    // so the literal route wins over the dynamic param.
    { path: "candidates/new", element: v1(<CandidateNew />) },
    { path: "candidates/:candidateId", element: v1(<CandidatePage />) },
    // Batch 30e — JD CRUD.
    // Phase 2.3 — `/jds/:jdId` is the read-only Detail; Edit moved to
    // `/jds/:jdId/edit`. Order matters: literal `/new` and `/:jdId/edit`
    // must come before the dynamic `/:jdId`.
    // NOTE: v1 keeps the `jds` name at the parked mount — the rename to `jobs`
    // is v2's, and rewriting v1's URLs would defeat the point of parking it.
    { path: "jds", element: v1(<Jds />) },
    { path: "jds/new", element: v1(<JdNew />) },
    { path: "jds/:jdId/edit", element: v1(<JdEdit />) },
    { path: "jds/:jdId", element: v1(<JdDetail />) },
    // Profile — standalone route (not nested under /settings) so it does
    // NOT highlight the left-nav Settings item. Mirrors the SA pattern
    // (/admin/profile → AdminProfile). The file stays at its existing path;
    // only the route mount point moves.
    { path: "profile", element: v1(<Profile />) },
    {
      path: "settings",
      children: [
        // App-shell nav declutter §2.3: the settings index is now a real
        // hub page (card grid), not a redirect to Profile.
        { index: true, element: v1(<SettingsHub />) },
        // Non-admin settings pages (any authenticated user):
        { path: "sessions", element: v1(<Sessions />) },
        // Admin-only settings pages (belt-and-suspenders client gate):
        {
          path: "team",
          element: <RequireAdmin>{v1(<Team />)}</RequireAdmin>,
        },
        {
          path: "roles",
          element: <RequireAdmin>{v1(<Roles />)}</RequireAdmin>,
        },
        // Batch 31a.2.3 — tenant security settings (mfa_required toggle).
        {
          path: "security",
          element: <RequireAdmin>{v1(<Security />)}</RequireAdmin>,
        },
        {
          path: "billing",
          element: <RequireAdmin>{v1(<Billing />)}</RequireAdmin>,
        },
        {
          path: "usage",
          element: <RequireAdmin>{v1(<Usage />)}</RequireAdmin>,
        },
        {
          path: "activity",
          element: <RequireAdmin>{v1(<Activity />)}</RequireAdmin>,
        },
        {
          // B-22b: /settings/ai is now member-accessible at the route
          // level so members see the read-only bias guardrails panel +
          // disabled textarea (HoD §5 visibility matrix). The page itself
          // gates Models / Ratings sections by isAdmin internally.
          path: "ai",
          element: v1(<AI />),
        },
        {
          path: "compliance",
          element: <RequireAdmin>{v1(<Compliance />)}</RequireAdmin>,
        },
        // Member-accessible AI oversight page (NOT RequireAdmin).
        // Design §5.1 / Q22-4: spec L2608(3) requires both roles to reach this.
        { path: "compliance/ai-oversight", element: v1(<AIOversight />) },
        { path: "integrations", element: v1(<Integrations />) },
      ],
    },
];

// ---------------------------------------------------------------------------
// v2 tree — THE DEFAULT TENANT UX as of PR4 ("the flip").
//
// Mounted at the tenant root `/:tenantSlug/*`. It previously lived at
// `/:tenantSlug/v2/*`; that path is now a compat redirect (see V2CompatRedirect).
//
// The guard stack is IDENTICAL to the one v1 had at this mount — RequireValidSlug
// → TenantContextProvider → RequireAuth → shell — so the flip changes which shell
// renders and nothing about who is allowed to render it (I14 guard parity).
// RequireValidSlug still runs first, so a bogus slug never reaches the JWT/slug
// cross-check.
//
// Named bindings (rather than an inline table) mirror `v1ShellElement`/`v1Children`
// and give `router.dualMount.test.ts` something to assert identity against.
const v2ShellElement = (
  <RequireValidSlug>
    <TenantContextProvider>
      <RequireAuth>
        <AppShellV2 />
      </RequireAuth>
    </TenantContextProvider>
  </RequireValidSlug>
);

const v2Children = [

    { index: true, element: <Navigate to="dashboard" replace /> },
    {
      path: "dashboard",
      element: <DashboardV2 />,
    },
    // UI-v2 Phase 1b-rem — Candidates + Intelligence.
    // "candidates/new" (1c) BEFORE "candidates/:candidateId" so the literal
    // wins over the dynamic param (same convention as v1).
    {
      path: "candidates",
      element: <CandidatesV2 />,
    },
    // UI-v2 Phase 1c — Add candidate. Literal BEFORE the dynamic id route.
    {
      path: "candidates/new",
      element: (
        <LazyRoute>
          <CandidateNewV2 />
        </LazyRoute>
      ),
    },
    {
      path: "candidates/:candidateId",
      element: <CandidatePageV2 />,
    },
    {
      path: "intelligence",
      element: (
        <LazyRoute>
          <IntelligenceDashboardV2 />
        </LazyRoute>
      ),
    },
    // PP-4 — Outcomes overview (post-placement tracking). Literal "outcomes"
    // BEFORE the dynamic "outcomes/:candidateId" placed-candidate view (PR 5),
    // same literal-before-param convention as candidates/jobs above.
    {
      path: "outcomes",
      element: <OutcomesOverviewV2 />,
    },
    // PP-7 — Model accuracy / calibration (Screen 3). LITERAL "outcomes/accuracy"
    // BEFORE the dynamic "outcomes/:candidateId" below, so "accuracy" is never
    // captured as a candidate id (same literal-before-param convention as the
    // jobs/candidates routes). Lazy (Recharts) → wrapped in <LazyRoute>.
    {
      path: "outcomes/accuracy",
      element: (
        <LazyRoute>
          <ModelAccuracyV2 />
        </LazyRoute>
      ),
    },
    // PP-5 — placed-candidate outcome view (dynamic, AFTER the literal
    // "outcomes" above). Lazy (Recharts) → wrapped in <LazyRoute>.
    {
      path: "outcomes/:candidateId",
      element: (
        <LazyRoute>
          <PlacedCandidateOutcomeV2 />
        </LazyRoute>
      ),
    },
    // UI-v2 Phase 1c — Jobs screens.
    // Order: literal "new" and "/:jdId/edit" BEFORE dynamic "/:jdId" (same
    // convention as v1 jds routes to avoid param-capture collisions).
    {
      path: "jobs",
      element: <JdsV2 />,
    },
    {
      path: "jobs/new",
      element: (
        <LazyRoute>
          <JdNewV2 />
        </LazyRoute>
      ),
    },
    {
      path: "jobs/:jdId/edit",
      element: (
        <LazyRoute>
          <JdEditV2 />
        </LazyRoute>
      ),
    },
    {
      path: "jobs/:jdId",
      element: (
        <LazyRoute>
          <JdDetailV2 />
        </LazyRoute>
      ),
    },
    // UI-v2 Phase 1c — Actions Log.
    {
      path: "journal",
      element: (
        <LazyRoute>
          <HiringJournalV2 />
        </LazyRoute>
      ),
    },
    // In-app Help Center — landing (`help`, the index doc) + one page per doc
    // (`help/:slug`). Available to ALL signed-in tenant users (no RequireAdmin).
    // Literal `help` is registered before the param route so it wins; a single
    // component serves both and renders an in-center not-found for unknown slugs.
    {
      path: "help",
      element: (
        <LazyRoute>
          <HelpCenterV2 />
        </LazyRoute>
      ),
    },
    {
      path: "help/:slug",
      element: (
        <LazyRoute>
          <HelpCenterV2 />
        </LazyRoute>
      ),
    },
    // UI-v2 Phase 1d — profile (standalone, not under /settings — mirrors v1).
    {
      path: "profile",
      element: (
        <LazyRoute>
          <ProfileV2 />
        </LazyRoute>
      ),
    },
    // UI-v2 Phase 1d — settings hub + all settings sub-pages.
    // Mirror v1 /:tenantSlug/settings subtree 1:1, adding RequireAdmin where v1 does.
    {
      path: "settings",
      children: [
        {
          index: true,
          element: (
            <LazyRoute>
              <SettingsHubV2 />
            </LazyRoute>
          ),
        },
        // Member-accessible:
        {
          path: "sessions",
          element: (
            <LazyRoute>
              <SessionsV2 />
            </LazyRoute>
          ),
        },
        {
          path: "ai",
          element: (
            <LazyRoute>
              <AIV2 />
            </LazyRoute>
          ),
        },
        {
          path: "integrations",
          element: (
            <LazyRoute>
              <IntegrationsV2 />
            </LazyRoute>
          ),
        },
        // Member-accessible AI oversight (NOT RequireAdmin — mirrors v1).
        {
          path: "compliance/ai-oversight",
          element: (
            <LazyRoute>
              <AIOversightV2 />
            </LazyRoute>
          ),
        },
        // Admin-only (belt-and-suspenders client gate):
        {
          path: "team",
          element: (
            <RequireAdmin>
              <LazyRoute>
                <TeamV2 />
              </LazyRoute>
            </RequireAdmin>
          ),
        },
        {
          path: "roles",
          element: (
            <RequireAdmin>
              <LazyRoute>
                <RolesV2 />
              </LazyRoute>
            </RequireAdmin>
          ),
        },
        {
          path: "security",
          element: (
            <RequireAdmin>
              <LazyRoute>
                <SecurityV2 />
              </LazyRoute>
            </RequireAdmin>
          ),
        },
        {
          path: "billing",
          element: (
            <RequireAdmin>
              <LazyRoute>
                <BillingV2 />
              </LazyRoute>
            </RequireAdmin>
          ),
        },
        {
          path: "usage",
          element: (
            <RequireAdmin>
              <LazyRoute>
                <UsageV2 />
              </LazyRoute>
            </RequireAdmin>
          ),
        },
        {
          path: "activity",
          element: (
            <RequireAdmin>
              <LazyRoute>
                <ActivityV2 />
              </LazyRoute>
            </RequireAdmin>
          ),
        },
        {
          path: "compliance",
          element: (
            <RequireAdmin>
              <LazyRoute>
                <ComplianceV2 />
              </LazyRoute>
            </RequireAdmin>
          ),
        },
        {
          path: "branding",
          element: (
            <RequireAdmin>
              <LazyRoute>
                <BrandingV2 />
              </LazyRoute>
            </RequireAdmin>
          ),
        },
      ],
    },
];

// Exported for `__tests__/router.dualMount.test.ts`, which asserts the v1 tree
// is mounted at BOTH `/:tenantSlug` and `/:tenantSlug/v1` off the SAME bindings.
// Not imported by app code — `router` below is the only runtime entry point.
export const appRoutes = [
  // Phase 1 — /dev/primitives showcase (no auth; HoD acceptance + visual baselines).
  { path: "/dev/primitives", element: <DevPrimitives /> },
  // B-14 Phase 1 — Hiring Intelligence Dashboard design-spike mocks.
  // No auth: sandbox surface with placeholder data; founder reviews on
  // prod after merge. Lazy-loaded code-split chunks per the design-spike
  // brief. React Router v6 normalizes trailing slashes during matching, so a
  // paste of either `/__mocks/dashboard/a` or `/__mocks/dashboard/a/` lands
  // cleanly without explicit redirect routes (those collide and cause silent
  // navigate-no-render).
  { path: "/__mocks/dashboard", element: <MockIndex /> },
  {
    path: "/__mocks/dashboard/a",
    element: (
      <LazyRoute>
        <MockDashboardA />
      </LazyRoute>
    ),
  },
  {
    path: "/__mocks/dashboard/b",
    element: (
      <LazyRoute>
        <MockDashboardB />
      </LazyRoute>
    ),
  },
  {
    path: "/__mocks/dashboard/c",
    element: (
      <LazyRoute>
        <MockDashboardC />
      </LazyRoute>
    ),
  },
  // B-24a Phase 1 — Super-Admin Dashboard design-spike mocks. No auth: sandbox
  // surface with placeholder platform data; founder reviews on prod after
  // merge. Single `__mocks` underscore (matches the B-14 precedent above).
  { path: "/__mocks/admin-dashboard", element: <AdminMockIndex /> },
  {
    path: "/__mocks/admin-dashboard/a",
    element: (
      <LazyRoute>
        <MockAdminDashboardA />
      </LazyRoute>
    ),
  },
  {
    path: "/__mocks/admin-dashboard/b",
    element: (
      <LazyRoute>
        <MockAdminDashboardB />
      </LazyRoute>
    ),
  },
  {
    path: "/__mocks/admin-dashboard/c",
    element: (
      <LazyRoute>
        <MockAdminDashboardC />
      </LazyRoute>
    ),
  },
  // Root → public signup (Batch 27d Lane 1). Marketing-site CTAs land here.
  { path: "/", element: <Navigate to="/auth/signup" replace /> },
  { path: "/404", element: <NotFound /> },
  // Public legal doc viewer — no auth required; linked from the signup +
  // accept-invite forms so users can read the docs before accepting.
  { path: "/legal/terms", element: <TermsOfServicePage /> },
  { path: "/legal/privacy", element: <PrivacyPolicyPage /> },
  { path: "/legal/dpa", element: <DataProcessingAddendumPage /> },
  { path: "/legal/acceptable-use", element: <AcceptableUsePolicyPage /> },
  { path: "/legal/account-data-privacy", element: <AccountDataPrivacyNoticePage /> },

  // ── AUTH ROUTES — flipped to v2 by PR4 ────────────────────────────────────
  //
  // These are the highest-traffic surfaces in the product and they are NOT
  // children of the `/:tenantSlug` shell — they are top-level siblings. Pointing
  // the shell at v2 therefore does NOT touch a single one of them. They must be
  // flipped explicitly, and that is what the block below does.
  //
  // This matters more than it looks. EVERY link the backend emails a real user
  // is one of these paths:
  //   welcome / temp-password  → /{slug}/login          (api/admin/__init__.py)
  //   password reset           → /{slug}/reset-password (services/auth/password_reset.py,
  //                                                      api/admin/__init__.py)
  //   workspace invite         → /{slug}/accept-invite  (api/invites.py)
  //   email verification       → /verify                (services/auth/{signup,verification}.py)
  //   demo provisioning        → /{slug}/login          (api/admin/demo.py)
  // Plus `lib/apiClient.ts`, which redirects to /{slug}/login (401) and
  // /{slug}/change-password (403 forced rotation) from OUTSIDE React.
  //
  // Had these been left on v1, the flip would have shipped a mixed tree: a user
  // clicks the welcome email, signs in on a v1 login form, and is dropped into
  // the v2 shell. Flipping them together is what makes the cutover coherent —
  // and it means the first v2 surface a real user touches is LoginV2, cold, from
  // an email. Every one of these already had a proven v2 twin under /v2.
  //
  // v1 is parked alongside each, never deleted.

  // Global (slug-less) auth.
  { path: "/auth/signup", element: <SignupV2 /> },
  { path: "/auth/v1/signup", element: v1(<Signup />) },
  { path: "/login", element: <SlugLandingV2 /> },
  { path: "/v1/login", element: v1(<SlugLanding />) },
  { path: "/verify", element: <VerifyEmailV2 /> },
  { path: "/v1/verify", element: v1(<VerifyEmail />) },

  // Slug-scoped auth — each wrapped in RequireValidSlug so a bogus /:slug/login
  // never renders the auth form.
  {
    path: "/:tenantSlug/login",
    element: (
      <RequireValidSlug>
        <LoginV2 />
      </RequireValidSlug>
    ),
  },
  {
    path: "/:tenantSlug/verify",
    element: (
      <RequireValidSlug>
        <VerifyEmailV2 />
      </RequireValidSlug>
    ),
  },
  {
    path: "/:tenantSlug/forgot-password",
    element: (
      <RequireValidSlug>
        <ForgotPasswordV2 />
      </RequireValidSlug>
    ),
  },
  {
    path: "/:tenantSlug/reset-password",
    element: (
      <RequireValidSlug>
        <ResetPasswordV2 />
      </RequireValidSlug>
    ),
  },
  {
    path: "/:tenantSlug/accept-invite",
    element: (
      <RequireValidSlug>
        <AcceptInviteV2 />
      </RequireValidSlug>
    ),
  },
  // WS-E — forced temp-password rotation. Sibling to the other auth routes
  // (outside RequireAuth) so a flagged user redirected here by the global 403
  // interceptor lands on a form that itself never 403s back (change-password is
  // backend-allowlisted). The interceptor also no-ops when already on this path.
  {
    path: "/:tenantSlug/change-password",
    element: (
      <RequireValidSlug>
        <ForcedPasswordChangeV2 />
      </RequireValidSlug>
    ),
  },

  // Parked v1 auth, slug-scoped. Siblings of the parked `/:tenantSlug/v1` shell
  // (which has no `login`/`verify`/… children, so these cannot collide with it).
  {
    path: "/:tenantSlug/v1/login",
    element: (
      <RequireValidSlug>{v1(<Login />)}</RequireValidSlug>
    ),
  },
  {
    path: "/:tenantSlug/v1/verify",
    element: (
      <RequireValidSlug>{v1(<VerifyEmail />)}</RequireValidSlug>
    ),
  },
  {
    path: "/:tenantSlug/v1/forgot-password",
    element: (
      <RequireValidSlug>{v1(<ForgotPassword />)}</RequireValidSlug>
    ),
  },
  {
    path: "/:tenantSlug/v1/reset-password",
    element: (
      <RequireValidSlug>{v1(<ResetPassword />)}</RequireValidSlug>
    ),
  },
  {
    path: "/:tenantSlug/v1/accept-invite",
    element: (
      <RequireValidSlug>{v1(<AcceptInvite />)}</RequireValidSlug>
    ),
  },
  {
    path: "/:tenantSlug/v1/change-password",
    element: (
      <RequireValidSlug>
        <ForcedPasswordChange />
      </RequireValidSlug>
    ),
  },
  // ── /v2/* COMPAT REDIRECTS — PR4 ─────────────────────────────────────────
  //
  // v2 used to live under a `/v2` segment. It is now the root, so `/v2` is no
  // longer where v2 IS — it is where v2 WAS. Everything under it redirects to the
  // canonical root path rather than 404ing: the founder's demo bookmarks, deep
  // links already shared, and browser history all keep working.
  //
  // ONE splat route covers the whole former v2 surface — the auth screens, the
  // shell, and every settings sub-page — because `V2CompatRedirect` rewrites the
  // path positionally and preserves search/hash (`?token=…` must survive).
  //
  // These redirects are the reason `useTenantBase()` could drop its `/v2` in one
  // line without stranding a single existing URL.
  { path: "/v2/login", element: <V2CompatRedirect /> },
  { path: "/v2/verify", element: <V2CompatRedirect /> },
  { path: "/auth/v2/signup", element: <V2CompatRedirect /> },
  { path: "/:tenantSlug/v2", element: <V2CompatRedirect /> },
  { path: "/:tenantSlug/v2/*", element: <V2CompatRedirect /> },

  // ── jds → jobs COMPAT REDIRECTS — PR4 ────────────────────────────────────
  //
  // The one URL CONTRACT CHANGE in the flip. v1 named this surface `jds`; v2
  // names it `jobs`, and `jobs` is canonical. Without these four entries every
  // `/acme/jds/:id` bookmark and history entry hard-404s the moment the root
  // becomes v2 — v2 simply has no `jds` route.
  //
  // Mounted at the top level (not as shell children) so the rewrite happens
  // before the auth/shell stack mounts: no shell flash, and the redirect works
  // for an unauthenticated visitor too. Order mirrors v1's own table — literal
  // `new` and `:jdId/edit` before the dynamic `:jdId`.
  { path: "/:tenantSlug/jds", element: <JdsCompatRedirect /> },
  { path: "/:tenantSlug/jds/new", element: <JdsCompatRedirect /> },
  { path: "/:tenantSlug/jds/:jdId/edit", element: <JdsCompatRedirect /> },
  { path: "/:tenantSlug/jds/:jdId", element: <JdsCompatRedirect /> },

  // v1 PARKED MOUNT — /:tenantSlug/v1/*. Same element, same children as the
  // root mount below (`v1Children`). Listed before `/:tenantSlug` to mirror how
  // the `/:tenantSlug/v2` spine is ordered; react-router ranks the static `v1`
  // segment above the dynamic slug regardless, and no v1 route is shaped
  // `/:tenantSlug/:param`, so `/acme/v1/dashboard` cannot collide with the root
  // mount's `/:tenantSlug/dashboard` (different segment counts).
  {
    path: "/:tenantSlug/v1",
    element: v1ShellElement,
    children: v1Children,
  },
  // ── THE FLIP — the tenant root now serves v2 ─────────────────────────────
  //
  // This is the line the whole v2 migration was sequenced to make small. PR1
  // cleaned the v2 pages, PR2 centralised the link base behind useTenantBase(),
  // PR3 parked v1 at its own mount. All of it existed so that turning v2 on for
  // every tenant user is: point this mount at the v2 bindings, and change
  // useTenantBase()'s one-line return.
  //
  // v1 is NOT deleted and NOT unreachable — it keeps its `/:tenantSlug/v1/*`
  // mount above, and reverting is re-pointing these two lines back.
  {
    path: "/:tenantSlug",
    element: v2ShellElement,
    children: v2Children,
  },
  { path: "*", element: <Navigate to="/404" replace /> },
];

const adminRoutes = [
  // Batch 27c — sibling to the RequireSuperAdmin-wrapped tree so anonymous
  // visitors can reach the SA login form without the guard redirecting.
  // Listed BEFORE the catch-all `*` so the wildcard doesn't shadow it.
  { path: "/admin/login", element: <AdminLogin /> },
  // WS-E — forced temp-password rotation (SA host). Sibling to /admin/login, so
  // a flagged super-admin redirected here by the global 403 interceptor lands
  // on a form outside RequireSuperAdmin (whose /auth/me would itself 403). The
  // change-password POST is backend-allowlisted; no 403 bounce.
  { path: "/admin/change-password", element: <ForcedPasswordChange /> },
  {
    path: "/",
    element: (
      <RequireSuperAdmin>
        <AdminShell />
      </RequireSuperAdmin>
    ),
    children: [
      // B-24b WS-3 — front door is now the Operator Console dashboard.
      { index: true, element: <Navigate to="dashboard" replace /> },
      { path: "tenants", element: <AdminTenants /> },
      { path: "tenants/new", element: <AdminTenantCreate /> },
      { path: "tenants/:tenantId", element: <AdminTenantDetail /> },
      {
        path: "tenants/:tenantId/model-overrides",
        element: <AdminModelOverrides />,
      },
      // B-22b — SA bias guardrails editor (host-scoped, novel SA surface).
      { path: "bias-guardrails", element: <AdminBiasGuardrails /> },
      // P1 (2026-06-15) — SA Global Settings → Unavatar PRO token (secret).
      {
        path: "global-settings/unavatar",
        element: <AdminGlobalSettingsUnavatar />,
      },
      { path: "triage", element: <AdminTriage /> },
      { path: "integrations", element: <AdminIntegrations /> },
      { path: "dashboard", element: <AdminDashboard /> },
      { path: "platform-health", element: <AdminPlatformHealth /> },
      // RBAC FE WS-D — super-admin users console. Inside RequireSuperAdmin like
      // every SA surface; the page itself additionally short-circuits on
      // impersonation (#8) and renders the backend's answer on 403/404 (#14).
      { path: "super-admins", element: <AdminSuperAdmins /> },
      { path: "demo", element: <AdminDemoReset /> },
      // B-11 — N-slug demo provisioning SA dashboard.
      // /demos/new MUST come BEFORE /demos/:slug so the literal route wins.
      { path: "demos", element: <AdminDemoList /> },
      { path: "demos/new", element: <AdminDemoProvision /> },
      { path: "demos/:slug", element: <AdminDemoDetail /> },
      { path: "profile", element: <AdminProfile /> },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
];

export const router = createBrowserRouter(host === "admin" ? adminRoutes : appRoutes);
